// What this browser actually exposes about the device. Everything is reported
// as the browser states it: memory is a capped hint, GPU strings are not
// hardware attestation, and anything unavailable stays `null`.

export interface HwInfo {
  cores: number | null;
  memoryGb: number | null; // navigator.deviceMemory: rounded, capped (often at 8)
  os: string;
  browser: string;
  screen: string;
  dpr: number;
  webgpu: boolean;
  gpuVendor: string | null;
  gpuArchitecture: string | null;
  gpuDescription: string | null;
  webglRenderer: string | null;
  limits: { maxBufferMb: number; maxWorkgroupInvocations: number; maxStorageMb: number } | null;
  fallbackAdapter: boolean | null;
}

let cached: Promise<HwInfo> | null = null;

function parseUa(): { os: string; browser: string } {
  const ua = navigator.userAgent;
  const uad = (navigator as unknown as { userAgentData?: { platform?: string; brands?: { brand: string; version: string }[] } }).userAgentData;
  let os = uad?.platform || '';
  if (!os) {
    if (/Windows/.test(ua)) os = 'Windows';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Mac OS X/.test(ua)) os = 'macOS';
    else if (/Linux/.test(ua)) os = 'Linux';
    else os = 'Unknown OS';
  }
  let browser = 'Unknown browser';
  const brand = uad?.brands?.find((b) => !/Not.?A.?Brand|Chromium/i.test(b.brand));
  if (brand) browser = `${brand.brand} ${brand.version}`;
  else {
    const m = /(Firefox|Edg|OPR|Chrome|Version)\/([\d.]+)/.exec(ua);
    if (m) browser = (m[1] === 'Version' ? 'Safari' : m[1] === 'Edg' ? 'Edge' : m[1] === 'OPR' ? 'Opera' : m[1]) + ' ' + m[2].split('.')[0];
  }
  return { os, browser };
}

function webglRenderer(): string | null {
  try {
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl2') || c.getContext('webgl')) as WebGLRenderingContext | null;
    if (!gl) return null;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const r = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return typeof r === 'string' ? r : null;
  } catch {
    return null;
  }
}

export async function gpuAdapter(): Promise<GPUAdapter | null> {
  try {
    if (!('gpu' in navigator) || !navigator.gpu) return null;
    return await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
  } catch {
    return null;
  }
}

export function detect(): Promise<HwInfo> {
  if (cached) return cached;
  cached = (async () => {
    const { os, browser } = parseUa();
    const adapter = await gpuAdapter();
    let info: { vendor?: string; architecture?: string; description?: string } | null = null;
    if (adapter) {
      const a = adapter as GPUAdapter & { info?: GPUAdapterInfo; requestAdapterInfo?: () => Promise<GPUAdapterInfo> };
      try {
        info = a.info ?? (await a.requestAdapterInfo?.()) ?? null;
      } catch {
        info = null;
      }
    }
    return {
      cores: navigator.hardwareConcurrency || null,
      memoryGb: (navigator as unknown as { deviceMemory?: number }).deviceMemory ?? null,
      os,
      browser,
      screen: `${screen.width}×${screen.height}`,
      dpr: Math.round((devicePixelRatio || 1) * 100) / 100,
      webgpu: !!adapter,
      gpuVendor: info?.vendor || null,
      gpuArchitecture: info?.architecture || null,
      gpuDescription: info?.description || null,
      webglRenderer: webglRenderer(),
      limits: adapter
        ? {
            maxBufferMb: Math.round(adapter.limits.maxBufferSize / 1048576),
            maxStorageMb: Math.round(adapter.limits.maxStorageBufferBindingSize / 1048576),
            maxWorkgroupInvocations: adapter.limits.maxComputeInvocationsPerWorkgroup,
          }
        : null,
      fallbackAdapter: adapter ? !!(adapter as unknown as { isFallbackAdapter?: boolean }).isFallbackAdapter : null,
    };
  })();
  return cached;
}

/** The most specific GPU label the browser offers, marked as browser-reported. */
export function gpuLabel(h: HwInfo): string {
  return h.gpuDescription || h.webglRenderer || [h.gpuVendor, h.gpuArchitecture].filter(Boolean).join(' ') || 'not reported';
}
